import { getCalendarWeek } from "@/lib/daycare-hotel/stayPlans";
import type { StayPlanStatus } from "@/hooks/useStayPlanStatus";

export type PlanPipState = "used" | "scheduled" | "available" | "adjustment";

export interface PlanPipAllocation {
  pips: PlanPipState[];
  overflowScheduledPips: PlanPipState[];
}

interface PlanUsage {
  usedOn: string;
  units: number;
  source: "hold" | "checkin";
}

function normalizeUnits(value: number) {
  return Math.max(0, Math.floor(value));
}

/** Distribui a cota entre uso confirmado, reservas e vagas, isolando excedentes agendados. */
export function buildQuotaPipAllocation({
  capacity,
  used,
  scheduled,
}: {
  capacity: number;
  used: number;
  scheduled: number;
}): PlanPipAllocation {
  const total = normalizeUnits(capacity);
  const usedCapped = Math.min(total, normalizeUnits(used));
  const scheduledUnits = normalizeUnits(scheduled);
  const scheduledCapped = Math.min(total - usedCapped, scheduledUnits);

  return {
    pips: [
      ...Array.from({ length: usedCapped }, () => "used" as const),
      ...Array.from({ length: scheduledCapped }, () => "scheduled" as const),
      ...Array.from({ length: total - usedCapped - scheduledCapped }, () => "available" as const),
    ],
    overflowScheduledPips: Array.from(
      { length: scheduledUnits - scheduledCapped },
      () => "scheduled" as const,
    ),
  };
}

function countUsageBySource(usages: PlanUsage[], period: { start: string; end: string }) {
  return usages.reduce(
    (totals, usage) => {
      if (usage.usedOn < period.start || usage.usedOn > period.end) return totals;
      const units = normalizeUnits(usage.units);
      if (usage.source === "checkin") totals.used += units;
      if (usage.source === "hold") totals.scheduled += units;
      return totals;
    },
    { used: 0, scheduled: 0 },
  );
}

/**
 * Os pips do ciclo, na ordem em que o design os mostra: as utilizadas primeiro,
 * depois as disponíveis da cota, e as de ajuste manual por último, destacadas.
 *
 * Um ajuste negativo encolhe o total em vez de virar pip — não existe "pip de
 * diária removida"; ela simplesmente não está mais lá.
 */
export function buildPlanPips({
  quota,
  carryoverUnits,
  adjustmentUnits,
  used,
}: {
  quota: number;
  carryoverUnits: number;
  adjustmentUnits: number;
  used: number;
}): PlanPipState[] {
  const base = Math.max(0, quota + carryoverUnits + Math.min(0, adjustmentUnits));
  const bonus = Math.max(0, adjustmentUnits);
  const total = base + bonus;
  const usedCapped = Math.min(used, total);

  return Array.from({ length: total }, (_, index) => {
    if (index < usedCapped) return "used";
    return index >= base ? "adjustment" : "available";
  });
}

export function buildWeeklyPlanPips({
  effectiveLimit,
  usages,
  week,
}: {
  effectiveLimit: number;
  usages: PlanUsage[];
  week: { start: string; end: string };
}): PlanPipState[] {
  return buildWeeklyPlanPipAllocation({ effectiveLimit, usages, week }).pips;
}

export function buildWeeklyPlanPipAllocation({
  effectiveLimit,
  usages,
  week,
}: {
  effectiveLimit: number;
  usages: PlanUsage[];
  week: { start: string; end: string };
}): PlanPipAllocation {
  const { used, scheduled } = countUsageBySource(usages, week);
  return buildQuotaPipAllocation({ capacity: effectiveLimit, used, scheduled });
}

/**
 * Quantas diárias de ajuste já foram gastas no ciclo de um plano semanal.
 *
 * A cota é da semana, então o que passa dela em qualquer semana do ciclo só
 * pôde sair do ajuste — é assim que se sabe se a diária extra ainda está de pé.
 */
export function countWeeklyAdjustmentUsed({
  usages,
  limit,
  cycle,
}: {
  usages: Array<{ usedOn: string; units: number }>;
  limit: number;
  cycle: { start: string; end: string } | null;
}): number {
  const perWeek = new Map<string, number>();

  for (const usage of usages) {
    if (cycle && (usage.usedOn < cycle.start || usage.usedOn > cycle.end)) continue;
    const week = getCalendarWeek(usage.usedOn).start;
    perWeek.set(week, (perWeek.get(week) ?? 0) + usage.units);
  }

  let overflow = 0;
  for (const used of perWeek.values()) overflow += Math.max(0, used - limit);
  return overflow;
}

export interface PlanMeter {
  /** `cycle` = cota mensal; `week` = cota semanal; `package` = pacote de diárias. */
  kind: "cycle" | "week" | "package";
  /** Marcadores da cota — do ciclo, da semana ou do pacote. Sem os de ajuste. */
  pips: PlanPipState[];
  /** Marcadores de reservas que excedem a capacidade da cota atual. */
  overflowScheduledPips: PlanPipState[];
  /**
   * Marcadores das diárias de ajuste, em trilha própria: elas seguem o ciclo, e
   * num plano semanal atravessam a virada da semana em vez de expirar com ela.
   */
  adjustmentPips: PlanPipState[];
  total: number;
  used: number;
  remaining: number;
  adjustmentUnits: number;
}

/**
 * O medidor de saldo, para os dois formatos de plano que têm saldo contável.
 *
 * Devolve `null` quando não há nada reconhecido para medir (cota, semana ou
 * pacote). Antes de o pacote ser tratado aqui, ele caía nesse ramo "sem cota"
 * e o card mostrava só um traço, apesar de o saldo estar ali em `remaining_days`.
 */
export function buildPlanMeter(status: StayPlanStatus): PlanMeter | null {
  const { contract, quota } = status;

  if (quota !== null) {
    const used = Math.max(
      0,
      quota + contract.carryoverUnits + status.adjustmentUnits - (status.balance.effective ?? 0),
    );
    const baseCapacity = Math.max(
      0,
      quota + contract.carryoverUnits + Math.min(0, status.adjustmentUnits),
    );
    const scheduled = status.cycle
      ? countUsageBySource(contract.usages, status.cycle).scheduled
      : 0;
    const allocation = buildQuotaPipAllocation({ capacity: baseCapacity, used, scheduled });
    const bonus = Math.max(0, status.adjustmentUnits);
    const usedInBase = allocation.pips.filter((pip) => pip === "used").length;
    const adjustmentUsed = Math.min(bonus, Math.max(0, used - usedInBase));
    return {
      kind: "cycle",
      pips: allocation.pips,
      overflowScheduledPips: allocation.overflowScheduledPips,
      adjustmentPips: Array.from({ length: bonus }, (_, index) =>
        index < adjustmentUsed ? "used" : "adjustment",
      ),
      total: baseCapacity + bonus,
      used,
      remaining: status.balance.projected ?? status.balance.effective ?? 0,
      adjustmentUnits: status.adjustmentUnits,
    };
  }

  // Cota da semana: o que sobra dela morre na segunda-feira, mas a diária de
  // ajuste é do ciclo e continua de pé na semana seguinte.
  if (status.weeklyQuota) {
    const { limit, effectiveLimit, usedThisWeek } = status.weeklyQuota;
    const allocation = buildWeeklyPlanPipAllocation({
      effectiveLimit,
      usages: contract.usages,
      week: status.weeklyQuota.week,
    });
    const usedInQuota = allocation.pips.filter((pip) => pip === "used").length;
    const extraTotal = Math.max(0, status.adjustmentUnits);
    const extraUsed = Math.min(
      extraTotal,
      countWeeklyAdjustmentUsed({ usages: contract.usages, limit, cycle: status.cycle }),
    );

    return {
      kind: "week",
      pips: allocation.pips,
      overflowScheduledPips: allocation.overflowScheduledPips,
      adjustmentPips: Array.from({ length: extraTotal }, (_, index) =>
        index < extraUsed ? "used" : "adjustment",
      ),
      total: effectiveLimit + extraTotal,
      used: usedInQuota + extraUsed,
      remaining: allocation.pips.filter((pip) => pip === "available").length + (extraTotal - extraUsed),
      adjustmentUnits: status.adjustmentUnits,
    };
  }

  if (contract.kind === "day_package" && contract.remainingDays !== null) {
    // O saldo do pacote já inclui os ajustes, mas os marcadores precisam
    // preservar também as diárias usadas antes deles. O saldo persistido
    // reconcilia contratos legados cujo histórico não está carregado. Ajustes
    // continuam no histórico, sem um marcador próprio.
    const adjustmentUnits = contract.adjustments.reduce((total, adjustment) => total + adjustment.delta, 0);
    const recordedUsed = contract.usages.reduce((total, usage) => total + usage.units, 0);
    const total = Math.max(
      0,
      (contract.includedDays ?? 0) + adjustmentUnits,
      contract.remainingDays + recordedUsed,
    );
    const used = Math.max(0, total - contract.remainingDays);
    return {
      kind: "package",
      pips: buildPlanPips({ quota: total, carryoverUnits: 0, adjustmentUnits: 0, used }),
      overflowScheduledPips: [],
      adjustmentPips: [],
      total,
      used,
      remaining: contract.remainingDays,
      adjustmentUnits: 0,
    };
  }

  return null;
}
